/** Anything smaller than this is float representation error, not money. */
const FLOAT_NOISE = 1e-9;

/**
 * Custom Final Total reversal (Protected Zone #5).
 *
 * When staff type a target "Grand Total" in the checkout, we back-solve the
 * lumpSumDiscount that makes `useQuoteSummary` land on that number. It inverts:
 *
 *   subtotal   = effectiveItemsTotal - discount + deliveryFee + cbdSurchargeFee
 *   grandTotal = subtotal * (1 + (hasGst ? gstRate : 0))
 *
 * ⇒ discount = effectiveItemsTotal + deliveryFee + cbdSurchargeFee
 *              - targetGrandTotal / (1 + (hasGst ? gstRate : 0))
 *
 * The GST divisor MUST follow the same `hasGst` gate as the summary
 * (Protected Zone #6 — only WSS charges GST). For the zero-GST issuing
 * companies (Smart Renovation, Pinki Beans) the divisor is 1: dividing by
 * (1 + gstRate) there strips a GST that was never added, landing the final
 * price ~9% low ("price before GST"). Pass `summary.hasGst` — never assume GST.
 *
 * Returns the required discount, which may be negative when the target is
 * above the computed total; the caller clamps with Math.max(0, …).
 *
 * The result keeps sub-cent precision on purpose — it must invert the GST
 * division exactly so the typed target reproduces to the cent (rounding it to
 * cents can land a WSS quote one cent off the typed figure). The ONLY value
 * touched is IEEE-754 noise: a magnitude below FLOAT_NOISE is snapped to exactly
 * 0. Without that, typing the un-discounted total leaves a residue (prod
 * QTE-2026-0335 V3: target $2060.10 on a $1890 pre-GST total → 2.27e-13) which
 * passes every `appliedDiscount > 0` guard and prints a phantom
 * "Lump Sum Discount -$0.00" line on the customer's quotation. Renderers
 * additionally hide any discount that rounds to $0.00 (`round2(x) > 0`).
 */
export function reverseFinalTotalToDiscount(params: {
  targetGrandTotal: number;
  effectiveItemsTotal: number;
  deliveryFee: number;
  cbdSurchargeFee: number;
  hasGst: boolean;
  gstRate: number;
}): number {
  const { targetGrandTotal, effectiveItemsTotal, deliveryFee, cbdSurchargeFee, hasGst, gstRate } =
    params;
  const gstDivisor = hasGst ? 1 + gstRate : 1;
  const targetSubtotal = targetGrandTotal / gstDivisor;
  const raw = effectiveItemsTotal + deliveryFee + cbdSurchargeFee - targetSubtotal;
  return Math.abs(raw) < FLOAT_NOISE ? 0 : raw;
}
